import { FIXTURE_PRODUCTS, findFixtureProducts } from "../domain/fixtures";
import type {
  CatalogClient,
  CatalogDataSource,
  CatalogMedia,
  CatalogMerchant,
  CatalogMessage,
  CatalogProduct,
  CatalogRating,
  CatalogSearchPage,
  CatalogVariant,
  EvidenceRecord,
  SearchCriteria,
} from "../domain/types";

const SHOPIFY_VERSION = "2026-04-08";
export const SHOPIFY_PUBLIC_PROBE_PROFILE = "https://shopify.dev/ucp/agent-profiles/2026-04-08/valid-with-capabilities.json";
const REQUIRED_TOOLS = ["search_catalog", "get_product", "lookup_catalog"] as const;

type CatalogErrorCode = "network" | "timeout" | "http" | "rate_limited" | "json_rpc" | "profile_negotiation" | "protocol_version" | "invalid_schema" | "unavailable" | "aborted" | "unknown";

export class CatalogClientError extends Error {
  constructor(public readonly code: CatalogErrorCode, message: string, public readonly retryable = true, public readonly httpStatus?: number) {
    super(message);
    this.name = "CatalogClientError";
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function asString(value: unknown, max: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const plain = value.replace(/<[^>]*>/gu, " ").replace(/\p{Cc}/gu, " ").normalize("NFKC").trim().replace(/\s+/gu, " ");
  return plain.slice(0, max);
}

function asBooleanOrNull(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function asSafeInteger(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

function asHttpsUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length > 2048) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

function listOfStrings(value: unknown, maxItems: number, maxLength = 500): string[] {
  if (typeof value === "string") return value.split(/\n+/u).map((item) => asString(item, maxLength)).filter((item): item is string => Boolean(item)).slice(0, maxItems);
  if (!Array.isArray(value)) return [];
  return value.map((item) => asString(item, maxLength)).filter((item): item is string => Boolean(item)).slice(0, maxItems);
}

function normalizeRating(value: unknown): CatalogRating | undefined {
  const rating = asRecord(value);
  if (!rating || typeof rating.value !== "number" || typeof rating.scale_min !== "number" || typeof rating.scale_max !== "number") return undefined;
  const count = asSafeInteger(rating.count);
  if (!Number.isFinite(rating.value) || !Number.isFinite(rating.scale_min) || !Number.isFinite(rating.scale_max) || count === undefined || rating.value < rating.scale_min || rating.value > rating.scale_max) return undefined;
  return { value: rating.value, scaleMin: rating.scale_min, scaleMax: rating.scale_max, count };
}

function normalizeMedia(value: unknown, maxItems: number): CatalogMedia[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const media = asRecord(item);
    if (media?.type !== "image") return [];
    const url = asHttpsUrl(media.url);
    if (!url) return [];
    return [{ type: "image" as const, url, altText: asString(media.alt_text, 240) ?? "", evidence: "merchant_fact" as const }];
  }).slice(0, maxItems);
}

function normalizeMerchant(value: unknown): CatalogMerchant | undefined {
  const raw = asRecord(value);
  const id = asString(raw?.id, 512);
  const name = asString(raw?.name, 120);
  const url = asHttpsUrl(raw?.url);
  if (!raw || !id?.startsWith("gid://shopify/Shop/") || !name || !url) return undefined;
  const links = Array.isArray(raw.links) ? raw.links : [];
  const knownTypes = new Set(["privacy_policy", "terms_of_service", "refund_policy", "shipping_policy", "other"]);
  const policyLinks = links.flatMap((item) => {
    const link = asRecord(item);
    const linkUrl = asHttpsUrl(link?.url);
    const type = asString(link?.type, 40);
    if (!linkUrl || !type) return [];
    const safeType = knownTypes.has(type) ? type as CatalogMerchant["policyLinks"][number]["type"] : "other";
    return [{ type: safeType, url: linkUrl }];
  }).slice(0, 8);
  const domain = asString(raw.domain, 253);
  return { id, name, url, ...(domain ? { domain } : {}), policyLinks };
}

function evidence(id: string, field: string, kind: EvidenceRecord["kind"], reason: string, observedAt: string, value?: string, sourceUrl?: string): EvidenceRecord {
  return { id: id.slice(0, 64), field: field.slice(0, 64), kind, reason: reason.slice(0, 160), observedAt, ...(value ? { value: value.slice(0, 500) } : {}), ...(sourceUrl ? { sourceUrl } : {}) };
}

export function normalizeCatalogProduct(value: unknown, observedAt = new Date().toISOString()): CatalogProduct | null {
  const raw = asRecord(value);
  const id = asString(raw?.id, 512);
  const title = asString(raw?.title, 240);
  if (!raw || !id || !(id.startsWith("gid://shopify/p/") || id.startsWith("gid://shopify/Product/")) || !title) return null;
  const descriptionRecord = asRecord(raw.description);
  const description = asString(descriptionRecord?.plain ?? raw.description, 2000);
  const media = normalizeMedia(raw.media, 12);
  const rating = normalizeRating(raw.rating);
  const evidenceRecords: EvidenceRecord[] = [
    evidence(`${id}-title`, "title", "merchant_fact", "Title supplied through Shopify Catalog.", observedAt, title),
  ];
  if (description) evidenceRecords.push(evidence(`${id}-description`, "description", "catalog_inference", "Shopify-enriched discovery text; not a verified merchant claim.", observedAt, description));
  if (rating) evidenceRecords.push(evidence(`${id}-rating`, "rating", "merchant_fact", "Rating supplied through Shopify Catalog.", observedAt, `${rating.value} of ${rating.scaleMax} from ${rating.count} ratings`));
  if (media[0]) evidenceRecords.push(evidence(`${id}-image`, "image", "merchant_fact", "Image supplied through Shopify Catalog and rendered live.", observedAt, media[0].altText, media[0].url));

  const variants: CatalogVariant[] = [];
  const rawVariants = Array.isArray(raw.variants) ? raw.variants : [];
  for (const item of rawVariants.slice(0, 50)) {
    const variant = asRecord(item);
    const variantId = asString(variant?.id, 512);
    const variantTitle = asString(variant?.title, 240);
    const variantUrl = asHttpsUrl(variant?.url);
    const money = asRecord(variant?.price);
    const amount = asSafeInteger(money?.amount);
    const currency = asString(money?.currency, 3);
    const merchant = normalizeMerchant(variant?.seller);
    if (!variant || !variantId?.startsWith("gid://shopify/ProductVariant/") || !variantTitle || !variantUrl || amount === undefined || !currency?.match(/^[A-Z]{3}$/u) || !merchant) continue;
    const availability = asRecord(variant.availability);
    const available = asBooleanOrNull(availability?.available);
    const variantDescriptionRecord = asRecord(variant.description);
    const variantDescription = asString(variantDescriptionRecord?.plain ?? variant.description, 2000);
    const variantMedia = normalizeMedia(variant.media, 4);
    const checkoutUrl = asHttpsUrl(variant.checkout_url);
    const variantRating = normalizeRating(variant.rating);
    const conditions = listOfStrings(variant.condition, 2, 20).filter((condition): condition is "new" | "secondhand" => condition === "new" || condition === "secondhand");
    const eligible = asRecord(variant.eligible);
    const selectedOptions = Array.isArray(variant.selected) ? variant.selected : Array.isArray(variant.selected_options) ? variant.selected_options : [];
    const normalizedOptions = selectedOptions.flatMap((option) => {
      const selected = asRecord(option);
      const name = asString(selected?.name, 64);
      const label = asString(selected?.label, 80);
      return name && label ? [{ name, label }] : [];
    });
    const rawInputs = Array.isArray(variant.inputs) ? variant.inputs : [];
    const lookupInputs = rawInputs.flatMap((input) => {
      const rawInput = asRecord(input);
      const inputId = asString(rawInput?.id, 512);
      const rawMatch = asString(rawInput?.match, 20);
      const match = rawMatch === "exact" || rawMatch === "featured" ? rawMatch : "unknown";
      return inputId ? [{ id: inputId, match } as const] : [];
    });
    const ids = [`${variantId}-price`, `${variantId}-availability`, `${variantId}-seller`];
    evidenceRecords.push(
      evidence(ids[0] ?? "price", "price", "merchant_fact", "Current price supplied through Shopify Catalog.", observedAt, `${amount} ${currency} minor units`),
      evidence(ids[1] ?? "availability", "availability", "merchant_fact", "Availability supplied through Shopify Catalog.", observedAt, available === null ? "Unknown" : available ? "Available" : "Unavailable"),
      evidence(ids[2] ?? "seller", "seller", "merchant_fact", "Seller identity supplied through Shopify Catalog.", observedAt, merchant.name, merchant.url),
    );
    if (conditions.length > 0) evidenceRecords.push(evidence(`${variantId}-condition`, "condition", "catalog_inference", "Condition is an inferred Shopify catalog field.", observedAt, conditions.join(", ")));
    variants.push({
      id: variantId,
      productId: id,
      title: variantTitle,
      ...(variantDescription ? { description: variantDescription } : {}),
      url: variantUrl,
      price: { amount, currency },
      available,
      media: variantMedia,
      merchant,
      ...(checkoutUrl ? { checkoutUrl } : {}),
      ...(variantRating ? { rating: variantRating } : {}),
      condition: conditions,
      ...(typeof eligible?.native_checkout === "boolean" ? { nativeCheckoutEligible: eligible.native_checkout } : {}),
      selectedOptions: normalizedOptions,
      lookupInputs,
      evidenceIds: ids,
    });
  }

  if (variants.length === 0) return null;
  const priceGroups = new Map<string, { min: number; max: number }>();
  for (const variant of variants) {
    const group = priceGroups.get(variant.price.currency);
    priceGroups.set(variant.price.currency, group ? { min: Math.min(group.min, variant.price.amount), max: Math.max(group.max, variant.price.amount) } : { min: variant.price.amount, max: variant.price.amount });
  }
  const optionsRaw = Array.isArray(raw.options) ? raw.options : [];
  const options = optionsRaw.flatMap((option) => {
    const optionRecord = asRecord(option);
    const name = asString(optionRecord?.name, 64);
    const values = Array.isArray(optionRecord?.values) ? optionRecord.values : [];
    if (!name) return [];
    return [{ name, values: values.flatMap((entry) => {
      const optionValue = asRecord(entry);
      const label = asString(optionValue?.label, 80);
      return label ? [{ label, available: asBooleanOrNull(optionValue?.available), exists: asBooleanOrNull(optionValue?.exists) }] : [];
    }) }];
  });
  const metadata = asRecord(raw.metadata);
  const inferredMetadata = {
    attributes: listOfStrings(metadata?.attributes, 16),
    techSpecs: listOfStrings(metadata?.tech_specs, 16),
    topFeatures: listOfStrings(metadata?.top_features, 16),
    uniqueSellingPoints: listOfStrings(metadata?.unique_selling_points, 16),
  };
  const unknownFields = ["delivery", "compatibility"].filter((field) => !evidenceRecords.some((record) => record.field === field));
  for (const field of unknownFields) evidenceRecords.push(evidence(`${id}-${field}`, field, "unknown", `${field[0]?.toUpperCase() ?? ""}${field.slice(1)} data is not available.`, observedAt));
  evidenceRecords.push(evidence(`${id}-availability-check`, "availability", "productist_check", "Productist checked the structured variant availability field.", observedAt, variants.some((variant) => variant.available === true) ? "At least one variant available" : "Availability unresolved"));
  return {
    id,
    title,
    ...(description ? { description } : {}),
    ...(rating ? { rating } : {}),
    media,
    options,
    inferredMetadata,
    variants,
    priceRanges: [...priceGroups].map(([currency, range]) => ({ currency, ...range })),
    evidence: evidenceRecords.slice(0, 32),
    observedAt,
  };
}

function normalizeMessages(value: unknown): CatalogMessage[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const record = asRecord(item);
    const text = asString(record?.content ?? record?.text, 240);
    if (!text) return [];
    const rawLevel = asString(record?.type ?? record?.level, 20);
    const level = rawLevel === "error" || rawLevel === "warning" ? rawLevel : "info";
    const code = asString(record?.code, 64);
    return [{ level, text, ...(code ? { code } : {}) }];
  });
}

interface RpcEnvelope { result: Record<string, unknown>; response: Response }

export class ShopifyCatalogClient implements CatalogClient {
  private toolsPromise: Promise<void> | null = null;
  private toolNames: string[] = [];
  private protocolVersion: string | undefined;
  private rpcSequence = 0;
  readonly profile: string;
  readonly profileMode: "productist_public" | "shopify_local_probe";

  constructor(private readonly endpoint = "/api/catalog", locationLike?: Pick<Location, "hostname" | "origin" | "protocol">, private readonly fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis)) {
    const runtime = locationLike ?? (typeof window !== "undefined" ? window.location : { hostname: "localhost", origin: "http://localhost", protocol: "http:" });
    const isLocal = runtime.hostname === "localhost" || runtime.hostname === "127.0.0.1" || runtime.protocol !== "https:";
    this.profile = isLocal ? SHOPIFY_PUBLIC_PROBE_PROFILE : new URL("/ucp/agent-profile.json", runtime.origin).toString();
    this.profileMode = isLocal ? "shopify_local_probe" : "productist_public";
  }

  getProtocolEvidence() {
    return { ...(this.protocolVersion ? { version: this.protocolVersion } : {}), toolNames: this.toolNames, profile: this.profile, profileMode: this.profileMode } as const;
  }

  private async rpc(method: "tools/list" | "tools/call", params: Record<string, unknown>, signal?: AbortSignal): Promise<RpcEnvelope> {
    const id = `productist-${Date.now()}-${++this.rpcSequence}`;
    let response: Response;
    try {
      response = await this.fetchImpl(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json, text/event-stream" },
        body: JSON.stringify({ jsonrpc: "2.0", id, method, params }),
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) throw new CatalogClientError("aborted", "The catalog request was cancelled.", true);
      throw new CatalogClientError("network", "The live catalog could not be reached.", true);
    }
    if (!response.ok) throw new CatalogClientError(response.status === 429 ? "rate_limited" : "http", "The live catalog returned an HTTP error.", response.status >= 500 || response.status === 429, response.status);
    let body: unknown;
    try { body = await response.json(); }
    catch { throw new CatalogClientError("invalid_schema", "The live catalog returned an unreadable response.", true); }
    const envelope = asRecord(body);
    if (!envelope || envelope.jsonrpc !== "2.0" || envelope.id !== id) throw new CatalogClientError("json_rpc", "The live catalog response did not match the request.", true);
    if (asRecord(envelope.error)) throw new CatalogClientError("json_rpc", "The live catalog rejected the request.", true);
    const result = asRecord(envelope.result);
    if (!result || result.isError === true) throw new CatalogClientError("json_rpc", "The live catalog could not use the request.", true);
    return { result, response };
  }

  private ensureTools(signal?: AbortSignal): Promise<void> {
    if (this.toolsPromise) return this.toolsPromise;
    this.toolsPromise = (async () => {
      const { result } = await this.rpc("tools/list", {}, signal);
      const tools = Array.isArray(result.tools) ? result.tools : [];
      const names = tools.map((tool) => asString(asRecord(tool)?.name, 64)).filter((name): name is string => Boolean(name));
      if (!REQUIRED_TOOLS.every((name) => names.includes(name))) throw new CatalogClientError("invalid_schema", "The live catalog does not advertise the required tools.", true);
      this.toolNames = names;
    })().catch((error: unknown) => {
      this.toolsPromise = null;
      throw error;
    });
    return this.toolsPromise;
  }

  private validateStructured(result: Record<string, unknown>, capability: "search" | "lookup"): Record<string, unknown> {
    const structured = asRecord(result.structuredContent);
    const ucp = asRecord(structured?.ucp);
    const capabilities = asRecord(ucp?.capabilities);
    const capabilityName = capability === "search" ? "dev.ucp.shopping.catalog.search" : "dev.ucp.shopping.catalog.lookup";
    if (!structured || !ucp || !capabilities) throw new CatalogClientError("invalid_schema", "The catalog response is missing its UCP envelope.", true);
    if (ucp.version !== SHOPIFY_VERSION) throw new CatalogClientError("protocol_version", "The live catalog protocol version is not supported.", true);
    if (ucp.status !== "success") throw new CatalogClientError("profile_negotiation", "The live catalog did not complete capability negotiation.", true);
    if (!Array.isArray(capabilities[capabilityName]) || !Array.isArray(capabilities["dev.shopify.catalog.global"])) throw new CatalogClientError("profile_negotiation", "The live catalog did not negotiate the required capability.", true);
    this.protocolVersion = SHOPIFY_VERSION;
    return structured;
  }

  private meta() { return { "ucp-agent": { profile: this.profile } }; }

  async search(criteria: SearchCriteria, signal?: AbortSignal): Promise<CatalogSearchPage> {
    await this.ensureTools(signal);
    const filters = criteria.filters;
    const ucpFilters: Record<string, unknown> = { available: filters?.available ?? true };
    if (filters?.categoryIds) ucpFilters.categories = filters.categoryIds;
    if (filters?.price) ucpFilters.price = { ...(filters.price.min !== undefined ? { min: filters.price.min } : {}), ...(filters.price.max !== undefined ? { max: filters.price.max } : {}) };
    if (filters?.shopIds) ucpFilters.shops = filters.shopIds;
    if (filters?.condition) ucpFilters.condition = filters.condition;
    if (filters?.shipsToCountry) ucpFilters.ships_to = { country: filters.shipsToCountry };
    if (filters?.shipsFromCountries) ucpFilters.ships_from = filters.shipsFromCountries.map((country) => ({ country }));
    if (filters?.attributes) ucpFilters.attributes = filters.attributes;
    if (filters?.rating) ucpFilters.rating = { variant: { ...(filters.rating.min !== undefined ? { min: filters.rating.min } : {}), ...(filters.rating.minCount !== undefined ? { min_count: filters.rating.minCount } : {}) } };
    if (filters?.priceTiers) ucpFilters.price_tier = filters.priceTiers;
    const context = criteria.context ? {
      ...(criteria.context.addressCountry ? { address_country: criteria.context.addressCountry } : {}),
      ...(criteria.context.language ? { language: criteria.context.language } : {}),
      ...(criteria.context.currency ? { currency: criteria.context.currency } : {}),
      ...(criteria.context.intent ? { intent: criteria.context.intent } : {}),
    } : undefined;
    const page = criteria.page ?? { mode: "first" as const, limit: 12 };
    const pagination = { limit: page.limit ?? 12, ...(page.mode === "next" ? { cursor: page.cursor } : {}) };
    const { result } = await this.rpc("tools/call", { name: "search_catalog", arguments: { meta: this.meta(), catalog: { query: criteria.query, ...(context ? { context } : {}), filters: ucpFilters, pagination, view: "offer" } } }, signal);
    const structured = this.validateStructured(result, "search");
    const observedAt = new Date().toISOString();
    const products = (Array.isArray(structured.products) ? structured.products : []).map((product) => normalizeCatalogProduct(product, observedAt)).filter((product): product is CatalogProduct => Boolean(product));
    const paginationResponse = asRecord(structured.pagination);
    const cursor = asString(paginationResponse?.cursor, 512);
    const totalCount = asSafeInteger(paginationResponse?.total_count);
    const hasNextPage = paginationResponse?.has_next_page === true && Boolean(cursor);
    const normalized: CatalogSearchPage = {
      querySignature: JSON.stringify({ query: criteria.query, context: criteria.context ?? {}, filters: filters ?? {}, sort: criteria.sort ?? "best_match" }),
      query: criteria.query,
      ...(criteria.context ? { context: criteria.context } : {}),
      filters: filters ?? {},
      sort: criteria.sort ?? "best_match",
      products,
      ...(totalCount !== undefined ? { totalCount } : {}),
      hasNextPage,
      ...(hasNextPage && cursor ? { cursor } : {}),
      messages: normalizeMessages(structured.messages),
      dataSource: "live",
      observedAt,
    };
    return normalized;
  }

  async getProduct(productId: string, signal?: AbortSignal): Promise<CatalogProduct> {
    await this.ensureTools(signal);
    const { result } = await this.rpc("tools/call", { name: "get_product", arguments: { meta: this.meta(), catalog: { id: productId, view: "summary" } } }, signal);
    const structured = this.validateStructured(result, "lookup");
    const product = normalizeCatalogProduct(structured.product);
    if (!product) throw new CatalogClientError("invalid_schema", "The product detail response could not be validated.", true);
    return product;
  }

  async lookup(ids: readonly string[], signal?: AbortSignal): Promise<readonly CatalogProduct[]> {
    await this.ensureTools(signal);
    const { result } = await this.rpc("tools/call", { name: "lookup_catalog", arguments: { meta: this.meta(), catalog: { ids, view: "offer" } } }, signal);
    const structured = this.validateStructured(result, "lookup");
    const observedAt = new Date().toISOString();
    return (Array.isArray(structured.products) ? structured.products : []).map((product) => normalizeCatalogProduct(product, observedAt)).filter((product): product is CatalogProduct => Boolean(product));
  }
}

export class CatalogGateway implements CatalogClient {
  private recovered = false;
  constructor(private readonly live: CatalogClient, private readonly initialMode: "live" | "test_fixture" | "failure_simulation" = "live") {}
  recoverWithFixture(): void { this.recovered = true; }
  get mode(): "live" | "test_fixture" | "failure_simulation" | "failure_fixture" { return this.recovered ? "failure_fixture" : this.initialMode; }
  getProtocolEvidence() { return this.live.getProtocolEvidence(); }

  async search(criteria: SearchCriteria, signal?: AbortSignal, source?: CatalogDataSource): Promise<CatalogSearchPage> {
    if (this.initialMode === "failure_simulation" && !this.recovered) throw new CatalogClientError("network", "The catalog failure was intentionally simulated for recovery testing.", true);
    if (this.initialMode === "live" && !this.recovered && source !== "failure_fixture" && source !== "test_fixture") return this.live.search(criteria, signal);
    const dataSource: CatalogDataSource = this.initialMode === "test_fixture" ? "test_fixture" : "failure_fixture";
    const all = findFixtureProducts(criteria);
    const page = criteria.page ?? { mode: "first" as const, limit: 12 };
    const offset = page.mode === "next" ? Number(page.cursor.replace("fixture:", "")) || 0 : 0;
    const limit = page.limit ?? 12;
    const products = all.slice(offset, offset + limit);
    const nextOffset = offset + products.length;
    const hasNextPage = nextOffset < all.length;
    return {
      querySignature: JSON.stringify({ query: criteria.query, filters: criteria.filters ?? {}, sort: criteria.sort ?? "best_match" }),
      query: criteria.query,
      ...(criteria.context ? { context: criteria.context } : {}),
      filters: criteria.filters ?? {},
      sort: criteria.sort ?? "best_match",
      products,
      totalCount: all.length,
      hasNextPage,
      ...(hasNextPage ? { cursor: `fixture:${nextOffset}` } : {}),
      messages: [{ level: "info", code: dataSource, text: dataSource === "test_fixture" ? "Deterministic test fixture results." : "Recovery fixture shown after a catalog failure." }],
      dataSource,
      observedAt: "2026-08-28T00:00:00.000Z",
    };
  }

  async getProduct(productId: string, signal?: AbortSignal): Promise<CatalogProduct> {
    if (this.initialMode === "live" && !this.recovered) return this.live.getProduct(productId, signal);
    const product = FIXTURE_PRODUCTS.find((item) => item.id === productId || item.variants.some((variant) => variant.id === productId));
    if (!product) throw new CatalogClientError("unavailable", "The product is not in the current fixture.", false);
    return product;
  }

  async lookup(ids: readonly string[], signal?: AbortSignal): Promise<readonly CatalogProduct[]> {
    if (this.initialMode === "live" && !this.recovered) return this.live.lookup(ids, signal);
    return FIXTURE_PRODUCTS.filter((product) => ids.includes(product.id) || product.variants.some((variant) => ids.includes(variant.id)));
  }
}

export function catalogModeFromSearch(search: string): "live" | "test_fixture" | "failure_simulation" {
  const mode = new URLSearchParams(search).get("catalog");
  return mode === "test" ? "test_fixture" : mode === "fail" ? "failure_simulation" : "live";
}
